import { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderView } from '../../../services/orders/orders.service';
import { ordersService } from '../../../services/orders/orders.service';
import type { SessionView } from '../../../services/sessions/sessions.service';

type UseActiveSessionPaidOrdersParams = {
  tenantId: string;
  employeeSession: string | null;
  activeSession: SessionView | null;
  isOrdersView: boolean;
  setError?: (message: string | null) => void;
};

export type UseActiveSessionPaidOrdersResult = {
  paidActive: boolean;
  paidOrders: OrderView[];
  isLoadingPaid: boolean;
  togglePaid: () => void;
  onOrderClosed: (orderId: number) => void;
  onOrderUpserted: (order: OrderView) => void;
};

// Summary: Lazy paid (status='closed') orders for the שולם toggle; fetch on ON, clear on OFF.
// Details: docs/function-library/web/contexts-pages.md#use-active-session-paid-orders
export function useActiveSessionPaidOrders({
  tenantId,
  employeeSession,
  activeSession,
  isOrdersView,
  setError,
}: UseActiveSessionPaidOrdersParams): UseActiveSessionPaidOrdersResult {
  const [paidActive, setPaidActive] = useState(false);
  const [paidOrders, setPaidOrders] = useState<OrderView[]>([]);
  const [isLoadingPaid, setIsLoadingPaid] = useState(false);

  const inFlightRef = useRef(false);
  const queuedRefetchRef = useRef(false);
  const paidActiveRef = useRef(false);
  const generationRef = useRef(0);
  const reloadRef = useRef<() => void>(() => undefined);

  const sessionId = activeSession?.id ?? null;

  // Summary: One guarded fetch of the active-session paid list; coalesces bursts via in-flight ref.
  // The generation ref discards a stale resolve after a reset (session/view change).
  const loadPaidOrders = useCallback(async () => {
    if (!employeeSession || !tenantId) {
      return;
    }
    if (inFlightRef.current) {
      queuedRefetchRef.current = true;
      return;
    }
    const generation = generationRef.current;
    inFlightRef.current = true;
    setIsLoadingPaid(true);
    try {
      const orders = await ordersService.listActiveSessionClosed(tenantId, employeeSession);
      if (generationRef.current !== generation) {
        return;
      }
      setPaidOrders(orders);
    } catch (loadError: unknown) {
      if (generationRef.current === generation && setError) {
        setError(loadError instanceof Error ? loadError.message : 'טעינת ההזמנות ששולמו נכשלה');
      }
    } finally {
      inFlightRef.current = false;
      if (generationRef.current === generation) {
        setIsLoadingPaid(false);
        if (queuedRefetchRef.current) {
          queuedRefetchRef.current = false;
          reloadRef.current();
        }
      }
    }
  }, [employeeSession, tenantId, setError]);

  reloadRef.current = () => {
    void loadPaidOrders();
  };

  // Summary: Flip the toggle; fetch fresh on turn-ON, clear the list on turn-OFF (Rules 21/22).
  const togglePaid = useCallback(() => {
    const next = !paidActiveRef.current;
    paidActiveRef.current = next;
    setPaidActive(next);
    if (next) {
      void loadPaidOrders();
    } else {
      setPaidOrders([]);
    }
  }, [loadPaidOrders]);

  // Summary: SSE delta on id-only order.closed — fetch ONLY that order through the paid predicate and
  // merge it (prepend if it matches, drop if it no longer does). No full refetch, no widened event.
  const onOrderClosed = useCallback(
    async (orderId: number) => {
      if (!paidActiveRef.current || !employeeSession || !tenantId) {
        return;
      }
      const generation = generationRef.current;
      try {
        const order = await ordersService.findActiveSessionClosed(tenantId, orderId, employeeSession);
        // Discard if a reset bumped the generation or the toggle was turned off mid-flight.
        if (generationRef.current !== generation || !paidActiveRef.current) {
          return;
        }
        setPaidOrders((prev) => {
          const without = prev.filter((existing) => existing.id !== orderId);
          // order.closed fires for merge/transfer/void closes too; the server returns null for those.
          return order ? [order, ...without] : without;
        });
      } catch {
        // Delta is best-effort; a failed lookup just leaves the list as-is until the next toggle refetch.
      }
    },
    [employeeSession, tenantId],
  );

  // Summary: order.upserted delta for a listed paid row (guest name, Wolt/Tenbis/Cibus lifecycle).
  const onOrderUpserted = useCallback((order: OrderView) => {
    if (!paidActiveRef.current) {
      return;
    }
    setPaidOrders((prev) =>
      prev.map((existing) =>
        existing.id === order.id
          ? {
            ...existing,
            guestName: order.guestName ?? null,
            woltStatus: order.woltStatus ?? null,
            woltNote: order.woltNote ?? null,
            tenbisStatus: order.tenbisStatus ?? null,
            tenbisNote: order.tenbisNote ?? null,
            cibusStatus: order.cibusStatus ?? null,
            cibusNote: order.cibusNote ?? null,
          }
          : existing,
      ),
    );
  }, []);

  // Summary: Reset all paid state; bump the generation so an in-flight resolve is discarded.
  const resetPaidState = useCallback(() => {
    generationRef.current += 1;
    inFlightRef.current = false;
    queuedRefetchRef.current = false;
    paidActiveRef.current = false;
    setPaidActive(false);
    setPaidOrders([]);
    setIsLoadingPaid(false);
  }, []);

  useEffect(() => {
    resetPaidState();
  }, [resetPaidState, sessionId, employeeSession]);

  useEffect(() => {
    if (!isOrdersView) {
      resetPaidState();
    }
  }, [resetPaidState, isOrdersView]);

  return { paidActive, paidOrders, isLoadingPaid, togglePaid, onOrderClosed, onOrderUpserted };
}
